/**
 * Customer branding: three base colours and everything derived from them.
 *
 * The whole visual system of the app (see apps/web/src/styles/theme.css)
 * hangs off a primary colour, a paper (surface) colour and an ink (text)
 * colour. A customer may set those three — per Customer through the API, or
 * per embed through iframe URL parameters — and every other tone (paper
 * steps, ink steps, lines, the primary ramp, cobalt accents) is computed
 * here so the derived look stays consistent whatever the base colours are.
 *
 * The defaults reproduce the "Justdroppit Flow v3" reference tokens, so an
 * unbranded customer sees exactly the reference design.
 *
 * Pure: no DOM, no I/O. The web app applies the result as CSS variables; the
 * API validates the same rules before storing a customer's branding.
 */

export interface Branding {
  primary?: string | null;
  paper?: string | null;
  ink?: string | null;
}

export interface ResolvedBranding {
  primary: string;
  paper: string;
  ink: string;
}

export type BrandingKey = keyof ResolvedBranding;
export const BRANDING_KEYS: readonly BrandingKey[] = [
  'primary',
  'paper',
  'ink',
];

/** The reference design's base colours. */
export const BRANDING_DEFAULTS: ResolvedBranding = {
  primary: '#2b4cff',
  paper: '#f4f1ea',
  ink: '#0d0d0d',
};

/** Body text on paper — WCAG AA. */
export const MIN_TEXT_CONTRAST = 4.5;
/** Primary controls against paper: visible, not necessarily readable text. */
export const MIN_ACCENT_CONTRAST = 2;
/** Foreground on a primary surface flips from white to ink below this. */
export const MIN_FOREGROUND_CONTRAST = 3;

const HEX_RE = /^#?([0-9a-fA-F]{6})$/;

export type Rgb = [number, number, number];
type Hsl = [number, number, number];

export function isHexColor(value: unknown): value is string {
  return typeof value === 'string' && HEX_RE.test(value.trim());
}

/** `#rrggbb`, lower case, with the hash — the one form stored and compared. */
export function normalizeHex(value: string): string {
  const match = HEX_RE.exec(value.trim());
  if (!match) throw new Error(`Not a hex colour: ${value}`);
  return `#${match[1].toLowerCase()}`;
}

export function hexToRgbTuple(hex: string): Rgb {
  const n = parseInt(normalizeHex(hex).slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function rgbTupleToHex([r, g, b]: Rgb): string {
  const part = (v: number) =>
    Math.max(0, Math.min(255, Math.round(v)))
      .toString(16)
      .padStart(2, '0');
  return `#${part(r)}${part(g)}${part(b)}`;
}

function rgbToHsl([r, g, b]: Rgb): Hsl {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l * 100];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === rn) h = (gn - bn) / d + (gn < bn ? 6 : 0);
  else if (max === gn) h = (bn - rn) / d + 2;
  else h = (rn - gn) / d + 4;
  return [h * 60, s * 100, l * 100];
}

function hslToRgb([h, s, l]: Hsl): Rgb {
  const hn = (((h % 360) + 360) % 360) / 360;
  const sn = Math.min(100, Math.max(0, s)) / 100;
  const ln = Math.min(100, Math.max(0, l)) / 100;
  if (sn === 0) {
    const v = Math.round(ln * 255);
    return [v, v, v];
  }
  const hue2rgb = (p: number, q: number, t: number): number => {
    let tt = t;
    if (tt < 0) tt += 1;
    if (tt > 1) tt -= 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };
  const q = ln < 0.5 ? ln * (1 + sn) : ln + sn - ln * sn;
  const p = 2 * ln - q;
  return [
    Math.round(hue2rgb(p, q, hn + 1 / 3) * 255),
    Math.round(hue2rgb(p, q, hn) * 255),
    Math.round(hue2rgb(p, q, hn - 1 / 3) * 255),
  ];
}

/** Linear blend; `t` is the share of `b`. */
export function mixRgb(a: Rgb, b: Rgb, t: number): Rgb {
  return [
    a[0] + (b[0] - a[0]) * t,
    a[1] + (b[1] - a[1]) * t,
    a[2] + (b[2] - a[2]) * t,
  ];
}

/** WCAG relative luminance, 0 (black) to 1 (white). */
export function relativeLuminance([r, g, b]: Rgb): number {
  const channel = (v: number) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** WCAG contrast ratio, 1 to 21. */
export function contrastRatio(a: Rgb, b: Rgb): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [light, dark] = la > lb ? [la, lb] : [lb, la];
  return (light + 0.05) / (dark + 0.05);
}

/**
 * Fill in the defaults for whatever is missing or malformed. Lenient on
 * purpose: this is the read path, and a bad value stored or passed on the
 * URL must degrade to the reference look, never to an unstyled page.
 */
export function resolveBranding(
  input: Branding | null | undefined
): ResolvedBranding {
  const pick = (key: BrandingKey): string => {
    const value = input?.[key];
    return isHexColor(value) ? normalizeHex(value) : BRANDING_DEFAULTS[key];
  };
  return { primary: pick('primary'), paper: pick('paper'), ink: pick('ink') };
}

export interface BrandingValidation {
  /** The branding as it would be stored: normalised, defaults filled in. */
  branding: ResolvedBranding;
  /** Empty when the input is acceptable. */
  errors: string[];
}

/**
 * The write path: every given value has to be a hex colour, and the three
 * together have to stay usable — readable text on paper, visible primary
 * controls on paper. Missing keys fall back to the defaults, `null` clears
 * a key back to its default.
 */
export function validateBranding(input: unknown): BrandingValidation {
  const errors: string[] = [];
  const given: Branding = {};

  if (input !== null && input !== undefined) {
    if (typeof input !== 'object' || Array.isArray(input)) {
      return {
        branding: { ...BRANDING_DEFAULTS },
        errors: ['branding must be an object'],
      };
    }
    const record = input as Record<string, unknown>;
    for (const key of Object.keys(record)) {
      if (!(BRANDING_KEYS as readonly string[]).includes(key)) {
        errors.push(`unknown key "${key}"`);
      }
    }
    for (const key of BRANDING_KEYS) {
      const value = record[key];
      if (value === undefined || value === null) continue;
      if (!isHexColor(value)) {
        errors.push(`${key} must be a hex colour like #2b4cff`);
        continue;
      }
      given[key] = value;
    }
  }

  const branding = resolveBranding(given);
  const paper = hexToRgbTuple(branding.paper);
  const ink = hexToRgbTuple(branding.ink);
  const primary = hexToRgbTuple(branding.primary);

  if (contrastRatio(paper, ink) < MIN_TEXT_CONTRAST) {
    errors.push(
      `ink on paper needs a contrast of at least ${MIN_TEXT_CONTRAST}:1`
    );
  }
  if (contrastRatio(paper, primary) < MIN_ACCENT_CONTRAST) {
    errors.push(
      `primary on paper needs a contrast of at least ${MIN_ACCENT_CONTRAST}:1`
    );
  }

  return { branding, errors };
}

const PRIMARY_RAMP_STEPS = [
  50, 100, 200, 300, 400, 500, 600, 700, 800, 900,
] as const;

/**
 * Blend per ramp step; 500 is the primary itself. Light steps blend towards
 * white, dark steps towards black — a blend keeps the hue where a pure
 * lightness change would over-saturate a vivid primary.
 */
const RAMP_BLEND: Record<number, { towards: 'white' | 'black'; t: number }> = {
  50: { towards: 'white', t: 0.95 },
  100: { towards: 'white', t: 0.9 },
  200: { towards: 'white', t: 0.82 },
  300: { towards: 'white', t: 0.68 },
  400: { towards: 'white', t: 0.45 },
  600: { towards: 'black', t: 0.08 },
  700: { towards: 'black', t: 0.3 },
  800: { towards: 'black', t: 0.42 },
  900: { towards: 'black', t: 0.55 },
};

const triplet = (rgb: Rgb): string => rgb.map((v) => Math.round(v)).join(' ');

/**
 * Lightness moved towards `towards` by `amount` points: paper steps get
 * darker on a light theme and lighter on a dark one, so "paper-2 is the
 * next surface in" holds either way.
 */
function stepLightness(l: number, towards: number, amount: number): number {
  return towards < l ? Math.max(0, l - amount) : Math.min(100, l + amount);
}

/**
 * Every CSS custom property the stylesheet reads, computed from the three
 * base colours. Colours are `#rrggbb`; the `--color-*` entries are RGB
 * triplets for Tailwind's alpha modifier (`rgb(var(--x) / <alpha-value>)`).
 */
export function brandingTokens(
  input: Branding | ResolvedBranding | null | undefined
): Record<string, string> {
  const branding = resolveBranding(input);
  const paper = hexToRgbTuple(branding.paper);
  const ink = hexToRgbTuple(branding.ink);
  const primary = hexToRgbTuple(branding.primary);
  const [ph, ps, pl] = rgbToHsl(paper);
  const [, , il] = rgbToHsl(ink);
  const [h, s, l] = rgbToHsl(primary);
  const white: Rgb = [255, 255, 255];

  const tokens: Record<string, string> = {};

  // Ink steps: the text colour, then two softer readings of it.
  tokens['--ink'] = branding.ink;
  tokens['--ink-2'] = rgbTupleToHex(mixRgb(ink, paper, 0.2));
  tokens['--ink-3'] = rgbTupleToHex(mixRgb(ink, paper, 0.42));
  tokens['--mute'] = `rgba(${ink.join(', ')}, 0.58)`;
  tokens['--line'] = `rgba(${ink.join(', ')}, 0.12)`;
  tokens['--line-2'] = `rgba(${ink.join(', ')}, 0.06)`;

  // Paper steps: same hue, a little less saturated, a little further in.
  tokens['--paper'] = branding.paper;
  tokens['--paper-2'] = rgbTupleToHex(
    hslToRgb([ph, ps * 0.94, stepLightness(pl, il, 5)])
  );
  tokens['--paper-3'] = rgbTupleToHex(
    hslToRgb([ph, ps * 0.87, stepLightness(pl, il, 12)])
  );

  // Primary and its accents. Light steps keep the hue and saturation and
  // only lift the lightness; a primary that is already light lifts less.
  const lift = (target: number) => (l < target ? target : l + (100 - l) / 2);
  tokens['--primary'] = branding.primary;
  tokens['--primary-fg'] =
    contrastRatio(primary, white) >= MIN_FOREGROUND_CONTRAST
      ? '#ffffff'
      : branding.ink;
  const soft = rgbTupleToHex(hslToRgb([h, s, lift(92)]));
  const deep = rgbTupleToHex(hslToRgb([h, s * 0.85, Math.min(l, 27)]));
  tokens['--primary-soft'] = soft;
  tokens['--primary-deep'] = deep;
  tokens['--cobalt-100'] = soft;
  tokens['--cobalt-300'] = rgbTupleToHex(hslToRgb([h, s, lift(75)]));
  tokens['--cobalt-500'] = branding.primary;
  tokens['--cobalt-700'] = rgbTupleToHex(
    hslToRgb([h, s * 0.74, Math.min(l, 40)])
  );
  tokens['--cobalt-900'] = deep;

  // The Tailwind ramp behind primary-{50..900}.
  const black: Rgb = [0, 0, 0];
  for (const step of PRIMARY_RAMP_STEPS) {
    const blend = RAMP_BLEND[step];
    const rgb = blend
      ? mixRgb(primary, blend.towards === 'white' ? white : black, blend.t)
      : primary;
    tokens[`--color-primary-${step}`] = triplet(rgb);
  }

  // Canvas workspace: a neutral backdrop leaning towards the primary, and
  // the selection frame in the primary itself (Fabric reads these).
  tokens['--color-canvas-bg'] = triplet(hslToRgb([h, s * 0.3, 91.8]));
  tokens['--color-canvas-selection'] = triplet(primary);

  return tokens;
}
